import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Plus, PencilSimple, Trash, X, MagnifyingGlass, ArrowCounterClockwise } from "@phosphor-icons/react";

const EMPTY = { title: "", price: "", compare_at_price: "", product_type: "", vendor: "", images: "", tags: "", description_html: "" };

export default function CatalogueAdmin() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [removed, setRemoved] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const limit = 20;

  const load = useCallback(async () => {
    const r = await api.get("/products", { params: { q: q || undefined, page, limit } });
    setItems(r.data.items);
    setTotal(r.data.total);
  }, [q, page]);

  const loadRemoved = () => api.get("/admin/products/removed").then(r => setRemoved(r.data));

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRemoved(); }, []);
  useEffect(() => () => setMsg(""), []);

  const submitSearch = (e) => { e.preventDefault(); setPage(1); setQ(search); };

  const startNew = () => { setEditing("new"); setForm(EMPTY); setMsg(""); };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title || "",
      price: p.price ?? "",
      compare_at_price: p.compare_at_price ?? "",
      product_type: p.product_type || "",
      vendor: p.vendor || "",
      images: (p.images || []).join("\n"),
      tags: (p.tags || []).join(", "),
      description_html: p.description_html || "",
    });
    setMsg("");
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg("");
    try {
      const payload = {
        title: form.title,
        price: parseFloat(form.price),
        compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
        product_type: form.product_type || "Other",
        vendor: form.vendor,
        images: form.images.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
        tags: form.tags.split(",").map(s => s.trim()).filter(Boolean),
        description_html: form.description_html,
      };
      if (editing === "new") {
        await api.post("/admin/products", payload);
        setMsg("Product added to catalogue.");
      } else {
        await api.patch(`/admin/products/${editing}`, payload);
        setMsg("Product updated. Manual edits are protected from sync overwrites.");
      }
      await load();
      cancel();
    } catch (e) {
      setMsg("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.title}" from the catalogue?${p.custom ? "" : " It will stay removed even after future syncs."}`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      setMsg("Product removed.");
      await Promise.all([load(), loadRemoved()]);
    } catch (e) {
      setMsg("Remove failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const restore = async (id) => {
    await api.post(`/admin/products/removed/${id}/restore`);
    setMsg("Restored. It will reappear after the next catalogue sync.");
    loadRemoved();
  };

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="px-6 md:px-12 py-8">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-4">
        <h2 className="text-2xl font-black tracking-tighter uppercase font-[Outfit]">Catalogue <span className="text-zinc-400">({total})</span></h2>
        {!editing && (
          <button data-testid="catalogue-new-btn" onClick={startNew} className="btn-brutal btn-red self-start">
            <Plus size={14} weight="bold" className="mr-2" /> Add Product
          </button>
        )}
      </div>
      {msg && <div data-testid="catalogue-msg" className="border border-zinc-900 bg-yellow-100 px-4 py-2 text-xs uppercase tracking-widest font-bold font-[Outfit] mb-4">{msg}</div>}

      {editing && (
        <form onSubmit={save} className="border border-zinc-900 p-6 mb-6 space-y-4 bg-zinc-50">
          <div className="flex justify-between items-center">
            <div className="font-[Outfit] font-bold uppercase tracking-tighter text-lg">{editing === "new" ? "New Product" : "Edit Product"}</div>
            <button type="button" onClick={cancel} className="p-1 hover:bg-zinc-900 hover:text-white"><X size={16} /></button>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Title *</label>
            <input data-testid="product-title-input" required className="input-brutal" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Price NZD *</label>
              <input data-testid="product-price-input" required type="number" step="0.01" min="0.01" className="input-brutal" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Compare-At Price (optional)</label>
              <input type="number" step="0.01" min="0" className="input-brutal" value={form.compare_at_price} onChange={e => setForm({ ...form, compare_at_price: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Category</label>
              <input className="input-brutal" placeholder="e.g. Tools" value={form.product_type} onChange={e => setForm({ ...form, product_type: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Vendor</label>
              <input className="input-brutal" value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Image URLs (one per line)</label>
            <textarea rows={3} className="input-brutal font-mono text-xs" placeholder="https://…" value={form.images} onChange={e => setForm({ ...form, images: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Tags (comma-separated)</label>
            <input className="input-brutal" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-1">Description (HTML)</label>
            <textarea rows={6} className="input-brutal font-mono text-xs" value={form.description_html} onChange={e => setForm({ ...form, description_html: e.target.value })} />
          </div>
          <button data-testid="product-save-btn" disabled={saving} className="btn-brutal btn-red">{saving ? "Saving…" : "Save Product"}</button>
        </form>
      )}

      <form onSubmit={submitSearch} className="flex gap-2 mb-4">
        <input data-testid="catalogue-search-input" className="input-brutal flex-1" placeholder="Search catalogue…" value={search} onChange={e => setSearch(e.target.value)} />
        <button data-testid="catalogue-search-btn" className="btn-brutal btn-blue"><MagnifyingGlass size={14} weight="bold" className="mr-2" /> Search</button>
      </form>

      <div className="border border-zinc-900">
        {items.length === 0 ? (
          <div className="p-6 text-center text-sm text-zinc-500">No products found.</div>
        ) : items.map(p => (
          <div key={p.id} data-testid={`catalogue-row-${p.id}`} className="flex items-center gap-4 p-4 border-b border-zinc-200 last:border-b-0">
            <div className="w-14 h-14 bg-zinc-100 border border-zinc-900 flex-shrink-0 overflow-hidden">
              {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold leading-tight truncate">{p.title}</div>
              <div className="text-xs text-zinc-500 mt-1">{p.category || p.product_type || "Other"}{p.in_stock === false && <span className="text-[#ff3333] font-bold"> · Out of stock</span>}</div>
            </div>
            <div className="font-bold font-[Outfit] whitespace-nowrap">${Number(p.price).toFixed(2)}</div>
            <span className={`tag hidden md:inline-flex ${p.custom ? "bg-[#0055ff] text-white border-[#0055ff]" : ""}`}>{p.custom ? "Custom" : p.locked ? "Edited" : "Synced"}</span>
            <button data-testid={`product-edit-${p.id}`} onClick={() => startEdit(p)} className="w-9 h-9 border border-zinc-900 flex items-center justify-center hover:bg-zinc-900 hover:text-white flex-shrink-0"><PencilSimple size={14} weight="bold" /></button>
            <button data-testid={`product-delete-${p.id}`} onClick={() => remove(p)} className="w-9 h-9 border border-zinc-900 flex items-center justify-center hover:bg-[#ff3333] hover:border-[#ff3333] hover:text-white flex-shrink-0"><Trash size={14} weight="bold" /></button>
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-3 mt-4">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-brutal disabled:opacity-40">Prev</button>
          <span className="text-xs uppercase tracking-widest font-bold font-[Outfit]">Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="btn-brutal disabled:opacity-40">Next</button>
        </div>
      )}

      {removed.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-black tracking-tighter uppercase font-[Outfit] mb-2">Removed From Sync ({removed.length})</h3>
          <p className="text-xs text-zinc-500 mb-3">These salelink products stay hidden even after a catalogue sync. Restore to bring one back (reappears on next sync).</p>
          <div className="border border-zinc-900">
            {removed.map(r => (
              <div key={r.id} className="flex items-center gap-4 p-3 border-b border-zinc-200 last:border-b-0">
                <div className="w-10 h-10 bg-zinc-100 border border-zinc-900 flex-shrink-0 overflow-hidden">
                  {r.image && <img src={r.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0 text-sm font-bold truncate">{r.title || r.id}</div>
                <button data-testid={`product-restore-${r.id}`} onClick={() => restore(r.id)} className="btn-brutal text-xs">
                  <ArrowCounterClockwise size={12} weight="bold" className="mr-1" /> Restore
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
